import React from 'react';
import { api } from '../api/client';
import type { CampaignData, User } from '../../shared/types';
import type { ToastTone } from '../components/ui/ToastStack';

export function useDataExport(
  currentUser: User | null,
  campaignData: CampaignData,
  setCampaignData: React.Dispatch<React.SetStateAction<CampaignData>>,
  notify?: (message: string, tone?: ToastTone) => void
) {
  const sendNotice = (message: string, tone: ToastTone) => {
    if (notify) {
      notify(message, tone);
      return;
    }
    alert(message);
  };

  const exportAllData = async () => {
    try {
      if (!currentUser) return;
      const exportData = await api.exportCampaign();

      const jsonString = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `campaign-${currentUser.username}-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      sendNotice('Error exporting data', 'error');
    }
  };

  const importData = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const importedData = JSON.parse(text) as { campaignData?: CampaignData };

      if (!importedData.campaignData) {
        sendNotice('Invalid data format', 'error');
        return;
      }

      const newData = await api.importCampaign(importedData.campaignData);
      setCampaignData(newData);
      sendNotice('Data imported successfully!', 'success');
    } catch (error) {
      console.error('Import error:', error);
      sendNotice('Error importing data. Please check the file format.', 'error');
    }
  };

  const getJSONPreview = () => {
    const previewData = {
      version: '1.0',
      exportDate: new Date().toISOString(),
      username: currentUser?.username || 'example_user',
      campaignData: campaignData
    };
    return JSON.stringify(previewData, null, 2);
  };

  return {
    exportAllData,
    importData,
    getJSONPreview,
  };
}
